// v3.19-1413 BLOCK 1 — RoleRoute
//
// Defense-in-depth frontend guard.  Backend already enforces role
// authorization via services/auth_roles.py (SSOT).  This component
// prevents unauthorized users from ever rendering an admin surface,
// so they see a clean redirect instead of a 403.
//
// Usage:
//   <RoleRoute allow={["ptc_admin","ptc_director","ptc_manager"]}>
//     <SomeAdminPage />
//   </RoleRoute>
//
// Guard semantics:
//   • Match the exact backend predicate (SSOT role set) for each page.
//   • Non-matching users are silently redirected to /clients.
//   • Impersonation ("View As") is honored via effectiveUser.role.
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const RoleRoute = ({ allow = [], children, fallback = '/clients' }) => {
  const { user, effectiveUser, loading, authChecked } = useAuth();
  if (loading || (!user && !authChecked)) return null;
  const role = (effectiveUser?.role || user?.role || '').toLowerCase();
  if (!allow.map((r) => r.toLowerCase()).includes(role)) {
    return <Navigate to={fallback} replace />;
  }
  return children;
};

export default RoleRoute;

// v3.19-1413 — canonical role sets mirroring backend services/auth_roles.py.
// Keep in sync with the SSOT.  These labels tell you which backend
// predicate the frontend guard mirrors.
export const ADMIN_SURFACE_ROLES = [
  'ptc_admin', 'ptc_director', 'ptc_manager',
];

export const DIRECTOR_TIER_ROLES = [
  'ptc_admin', 'company_director', 'manager_director',
];

export const DIRECTOR_TIER_EXTENDED_ROLES = [
  'ptc_admin', 'admin', 'super_admin',
  'ptc_director', 'director', 'manager_director',
];

export const PTC_ADMIN_STRICT_ROLES = ['ptc_admin'];

export const ADMIN_DIRECTOR_ROLES = [
  'ptc_admin', 'admin', 'ptc_director',
  'company_admin', 'company_director',
];

export const PTC_ADMIN_OR_DIRECTOR_ROLES = ['ptc_admin', 'ptc_director'];
